@let lowStockItem = lowStock$ | async;
@let expiringItem = expiringSoon$ | async;
@let superItem = superintendentStatus$ | async;
@let stats = orderStats$ | async;
@let custStats = customerStats$ | async;

<app-layout-toolbar
  title="Dashboard"
  [crumbs]="['Dashboard']"
  [showBackButton]="false">
  <button
    mat-icon-button
    [matTooltip]="isOffline() ? 'Reconnect to refresh' : 'Refresh'"
    [disabled]="isOffline()"
    (click)="refresh()">
    <mat-icon svgIcon="mat:refresh"></mat-icon>
  </button>
</app-layout-toolbar>

<div class="container p-6">
  @if (isOffline()) {
    @let lastLoadedAt = lastLoadedAt$ | async;
    <div
      class="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-6 text-sm text-default"
      role="status"
      aria-live="polite">
      <mat-icon
        svgIcon="mat:cloud_off"
        class="text-amber-600 flex-none"></mat-icon>
      <div>
        @if (lastLoadedAt) {
          <p class="font-semibold m-0">
            Showing data from {{ lastLoadedAt | date: "medium" }}
          </p>
          <p class="m-0 text-secondary">
            You're offline — these figures refresh once you reconnect.
          </p>
        } @else {
          <p class="font-semibold m-0">No dashboard data on this device yet</p>
          <p class="m-0 text-secondary">
            Open the dashboard while online once and it will be available
            offline.
          </p>
        }
      </div>
    </div>
  }
  <!-- Welcome banner -->
  @if (user$ | async; as user) {
    <div
      class="bg-primary/10 rounded-xl p-6 mb-6 text-primary flex items-center justify-between">
      <div>
        <h2 class="text-xl font-bold mb-1">
          Welcome back, {{ user.firstName }}!
        </h2>
        <p class="opacity-80 text-sm">
          Here's what's happening at your pharmacy today.
        </p>
      </div>
      <mat-icon
        svgIcon="mat:local_pharmacy"
        class="opacity-40 w-16 h-16"
        style="font-size: 64px"></mat-icon>
    </div>
  }

  <!-- Branch picker -->
  <div class="mb-6">
    <app-branch-selector
      mode="multiple"
      label="Filter by branches"
      [value]="(selectedBranchIds$ | async) ?? []"
      (branchesChange)="onBranchChange($event)">
    </app-branch-selector>
  </div>

  <!-- KPI cards with trends -->
  <div
    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
    <!-- #1 Today's revenue -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-xs text-secondary font-medium"
              >Today's Revenue</span
            >
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-600/10 text-primary-600">
              <mat-icon
                svgIcon="mat:payments"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
          </div>
          @if (isLoaded(item)) {
            <p class="text-2xl font-bold text-default">
              {{ (todayRevenue$ | async) ?? 0 | amountToMajor | price }}
            </p>
            @if (revenueTodayTrend$ | async; as trend) {
              <div class="flex items-center gap-1 text-xs">
                <mat-icon
                  [svgIcon]="
                    trend.isPositive ? 'mat:trending_up' : 'mat:trending_down'
                  "
                  [class]="trendColorClass(trend)"
                  style="font-size: 14px; width: 14px; height: 14px"></mat-icon>
                <span [class]="trendColorClass(trend)"
                  >{{ trend.isPositive ? "+" : ""
                  }}{{ trend.changePercent }}%</span
                >
                <span class="text-secondary">vs yesterday</span>
              </div>
            }
          } @else {
            <div class="h-8 w-24 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>

    <!-- #2 Today's orders -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-xs text-secondary font-medium"
              >Today's Orders</span
            >
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-green-600/10 text-green-600">
              <mat-icon
                svgIcon="mat:receipt_long"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
          </div>
          @if (isLoaded(item)) {
            <p class="text-2xl font-bold text-default">
              {{ (todayOrderCount$ | async) ?? 0 }}
            </p>
            @if (ordersTodayTrend$ | async; as trend) {
              <div class="flex items-center gap-1 text-xs">
                <mat-icon
                  [svgIcon]="
                    trend.isPositive ? 'mat:trending_up' : 'mat:trending_down'
                  "
                  [class]="trendColorClass(trend)"
                  style="font-size: 14px; width: 14px; height: 14px"></mat-icon>
                <span [class]="trendColorClass(trend)"
                  >{{ trend.isPositive ? "+" : ""
                  }}{{ trend.changePercent }}%</span
                >
                <span class="text-secondary">vs yesterday</span>
              </div>
            }
          } @else {
            <div class="h-8 w-24 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>

    <!-- #3 Pending pay-by-link -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <app-kpi-card
          label="Pending Pay-by-Link"
          icon="mat:link"
          colorClass="bg-purple-600/10 text-purple-600"
          [item]="item"
          [value]="(pendingOrders$ | async) ?? 0"
          [link]="orderPaths.List"
          (retry)="retryOrderStats()">
        </app-kpi-card>
      }
    </div>

    <!-- #7 Out of stock -->
    <div *appHasPermission="PERMS.INVENTORY.READ">
      @if (outOfStock$ | async; as item) {
        <app-kpi-card
          label="Out of Stock"
          icon="mat:remove_shopping_cart"
          colorClass="bg-red-600/10 text-red-600"
          [item]="item"
          [value]="item.data"
          [link]="inventoryPaths.Stock"
          (retry)="retryOutOfStock()">
        </app-kpi-card>
      }
    </div>

    <!-- #9 Week order count with trend -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <a
          [routerLink]="orderPaths.List"
          [queryParams]="(weekRange$ | async) ?? {}"
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col gap-2 hover:shadow-md transition-shadow cursor-pointer">
          <div class="flex items-center justify-between">
            <span class="text-xs text-secondary font-medium"
              >Orders This Week</span
            >
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-600/10 text-blue-600">
              <mat-icon
                svgIcon="mat:shopping_bag"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
          </div>
          @if (isLoaded(item)) {
            <p class="text-2xl font-bold text-default">
              {{ (weekOrderCount$ | async) ?? 0 }}
            </p>
            @if (ordersWeekTrend$ | async; as trend) {
              <div class="flex items-center gap-1 text-xs">
                <mat-icon
                  [svgIcon]="
                    trend.isPositive ? 'mat:trending_up' : 'mat:trending_down'
                  "
                  [class]="trendColorClass(trend)"
                  style="font-size: 14px; width: 14px; height: 14px"></mat-icon>
                <span [class]="trendColorClass(trend)"
                  >{{ trend.isPositive ? "+" : ""
                  }}{{ trend.changePercent }}%</span
                >
                <span class="text-secondary">vs last week</span>
              </div>
            }
          } @else {
            <div class="h-8 w-24 bg-base rounded animate-pulse"></div>
          }
          <div class="flex items-center gap-1 text-xs text-primary-600 mt-auto">
            <span>View details</span>
            <mat-icon
              svgIcon="mat:chevron_right"
              class="w-3 h-3"
              style="font-size: 14px"></mat-icon>
          </div>
        </a>
      }
    </div>
  </div>

  <!-- Charts -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <!-- Payment method donut -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div class="bg-foreground rounded-xl border border-divider p-4 h-full">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-600/10 text-primary-600">
              <mat-icon
                svgIcon="mat:account_balance_wallet"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Payment Methods</span
            >
          </div>
          @if (isLoaded(item)) {
            @if (paymentMethodDonut$ | async; as chart) {
              <app-chart [data]="chart"></app-chart>
            } @else {
              <p class="text-sm text-secondary text-center py-6">
                No payments today
              </p>
            }
          } @else {
            <div class="h-44 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>

    <!-- Order source donut -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div class="bg-foreground rounded-xl border border-divider p-4 h-full">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-600/10 text-purple-600">
              <mat-icon
                svgIcon="mat:source"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Order Sources</span
            >
          </div>
          @if (isLoaded(item)) {
            @if (orderSourceDonut$ | async; as chart) {
              <app-chart [data]="chart"></app-chart>
            } @else {
              <p class="text-sm text-secondary text-center py-6">
                No orders today
              </p>
            }
          } @else {
            <div class="h-44 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>

    <!-- Revenue trend (area chart from order stats dailyBreakdown) -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col">
          <div class="flex items-center gap-2 mb-2">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-green-600/10 text-green-600">
              <mat-icon
                svgIcon="mat:show_chart"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Revenue Trend (7 days)</span
            >
          </div>
          @if (isLoaded(item)) {
            <p class="text-xl font-bold text-default mb-2">
              {{ (weekRevenue$ | async) ?? 0 | amountToMajor | price }}
            </p>
            @if (revenueAreaChart$ | async; as chart) {
              <app-chart [data]="chart"></app-chart>
            } @else {
              <p class="text-sm text-secondary text-center py-4">No data</p>
            }
          } @else {
            <div class="h-32 bg-base rounded animate-pulse flex-auto"></div>
          }
        </div>
      }
    </div>

    <!-- Daily orders bar chart -->
    <div *appHasPermission="PERMS.ORDERS.READ">
      @if (stats; as item) {
        <div class="bg-foreground rounded-xl border border-divider p-4 h-full">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-600/10 text-blue-600">
              <mat-icon
                svgIcon="mat:bar_chart"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Daily Orders (7 days)</span
            >
          </div>
          @if (isLoaded(item)) {
            @if (ordersBarChart$ | async; as chart) {
              <app-chart [data]="chart"></app-chart>
            } @else {
              <p class="text-sm text-secondary text-center py-6">
                No orders this week
              </p>
            }
          } @else {
            <div class="h-44 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Expenses this month -->
  @if (canReadExpenses()) {
    <div class="w-full py-2">
      <app-expenses-month-card
        [summary]="expenseSummary()"
        [linkQueryParams]="expenseMonthQuery()"
        (retry)="retryExpenses()"></app-expenses-month-card>
    </div>
  }

  <!-- Customer KPIs with trends + chart -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <!-- #14 New customers -->
    <div *appHasPermission="PERMS.CUSTOMERS.READ">
      @if (custStats; as item) {
        <div
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-xs text-secondary font-medium"
              >New Customers (7 days)</span
            >
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-600/10 text-blue-600">
              <mat-icon
                svgIcon="mat:person_add"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
          </div>
          @if (isLoaded(item)) {
            <p class="text-2xl font-bold text-default">
              {{ (newCustomersThisWeek$ | async) ?? 0 }}
            </p>
            @if (newCustomersTrend$ | async; as trend) {
              <div class="flex items-center gap-1 text-xs">
                <mat-icon
                  [svgIcon]="
                    trend.isPositive ? 'mat:trending_up' : 'mat:trending_down'
                  "
                  [class]="trendColorClass(trend)"
                  style="font-size: 14px; width: 14px; height: 14px"></mat-icon>
                <span [class]="trendColorClass(trend)"
                  >{{ trend.isPositive ? "+" : ""
                  }}{{ trend.changePercent }}%</span
                >
                <span class="text-secondary">vs last week</span>
              </div>
            }
          } @else {
            <div class="h-8 w-24 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>

    <!-- #15 Returning rate -->
    <div *appHasPermission="PERMS.CUSTOMERS.READ">
      @if (custStats; as item) {
        <app-kpi-card
          label="Returning Customer Rate"
          icon="mat:repeat"
          colorClass="bg-green-600/10 text-green-600"
          [item]="item"
          [value]="item.data?.returningRate ?? 0"
          suffix="%"
          [link]="customersPaths.List"
          (retry)="retryCustomerStats()">
        </app-kpi-card>
      }
    </div>

    <!-- New customers daily chart -->
    <div *appHasPermission="PERMS.CUSTOMERS.READ" class="md:col-span-2">
      @if (custStats; as item) {
        <div class="bg-foreground rounded-xl border border-divider p-4 h-full">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-600/10 text-blue-600">
              <mat-icon
                svgIcon="mat:group_add"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Daily Signups (7 days)</span
            >
          </div>
          @if (isLoaded(item)) {
            @if (newCustomersChart$ | async; as chart) {
              <app-chart [data]="chart"></app-chart>
            } @else {
              <p class="text-sm text-secondary text-center py-4">No data</p>
            }
          } @else {
            <div class="h-36 bg-base rounded animate-pulse"></div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Inventory + top products -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
    <!-- #5 Low stock -->
    <div *appHasPermission="PERMS.INVENTORY.READ">
      @if (lowStockItem; as item) {
        <app-list-card
          title="Low Stock"
          icon="mat:warning"
          colorClass="bg-orange-600/10 text-orange-600"
          [item]="item"
          [count]="item.data?.totalItems ?? null"
          [link]="inventoryPaths.Stock"
          (retry)="retryLowStock()">
          @if (item.data; as summary) {
            @for (i of summary.items; track i.variantId) {
              <div
                class="border border-divider rounded-lg p-2 flex items-center justify-between">
                <p class="text-sm text-default truncate flex-auto">
                  {{ i.displayName }}
                </p>
                <div class="text-right ml-2">
                  <span
                    class="text-xs font-semibold"
                    [class]="
                      i.quantityAvailable === 0
                        ? 'text-red-600'
                        : 'text-orange-600'
                    "
                    >{{ i.quantityAvailable }}</span
                  >
                  <span class="text-xs text-secondary">
                    / {{ i.currentReorderPoint }}</span
                  >
                </div>
              </div>
            } @empty {
              <p class="text-sm text-secondary text-center py-4">
                All stock levels healthy
              </p>
            }
          }
        </app-list-card>
      }
    </div>

    <!-- #6 Expiring soon -->
    <div *appHasPermission="PERMS.INVENTORY.READ">
      @if (expiringItem; as item) {
        <app-list-card
          title="Expiring Soon"
          icon="mat:event_busy"
          colorClass="bg-red-600/10 text-red-600"
          [item]="item"
          [count]="item.data?.totalItems ?? null"
          [link]="inventoryPaths.Dashboard"
          (retry)="retryExpiringSoon()">
          @if (item.data; as summary) {
            @for (i of summary.items; track i.variantId + i.batchNumber) {
              <div class="border border-divider rounded-lg p-2">
                <div class="flex items-center justify-between">
                  <p class="text-sm text-default truncate">
                    {{ i.displayName }}
                  </p>
                  <span
                    class="text-xs px-1.5 py-0.5 rounded-full font-medium"
                    [class]="
                      i.status === 'expired'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-orange-100 text-orange-700'
                    ">
                    {{ i.status === "expired" ? "Expired" : "Near Expiry" }}
                  </span>
                </div>
                <p class="text-xs text-secondary mt-0.5">
                  Batch {{ i.batchNumber }} ·
                  {{ i.expiryDate | date: "mediumDate" }} ·
                  {{ i.quantityOnHand }} units
                </p>
              </div>
            } @empty {
              <p class="text-sm text-secondary text-center py-4">
                No batches expiring soon
              </p>
            }
          }
        </app-list-card>
      }
    </div>

    <!-- #10 Top products table -->
    <div *appHasPermission="PERMS.REPORTS.READ">
      @if (topProducts$ | async; as item) {
        <div
          class="bg-foreground rounded-xl border border-divider p-4 h-full flex flex-col">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center bg-green-600/10 text-green-600">
              <mat-icon
                svgIcon="mat:trending_up"
                class="w-4 h-4"
                style="font-size: 18px"></mat-icon>
            </div>
            <span class="text-sm font-medium text-default flex-auto"
              >Top Products (7 days)</span
            >
          </div>
          @if (isLoaded(item)) {
            <div
              class="overflow-x-auto rounded-lg border border-divider flex-auto responsive table-wrapper">
              <table
                mat-table
                [dataSource]="item.data"
                class="erp-table w-full">
                <ng-container matColumnDef="rank">
                  <th
                    mat-header-cell
                    *matHeaderCellDef
                    class="text-xs uppercase text-secondary font-medium w-10">
                    #
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let p; let i = index"
                    class="text-sm font-semibold text-secondary w-10">
                    {{ i + 1 }}
                  </td>
                </ng-container>
                <ng-container matColumnDef="product">
                  <th
                    mat-header-cell
                    *matHeaderCellDef
                    class="text-xs uppercase text-secondary font-medium">
                    Product
                  </th>
                  <td mat-cell *matCellDef="let p" class="py-2">
                    <p class="text-sm font-medium text-default truncate">
                      {{ p.variantName }}
                    </p>
                    <p class="text-xs text-secondary">{{ p.sku }}</p>
                  </td>
                </ng-container>
                <ng-container matColumnDef="qty">
                  <th
                    mat-header-cell
                    *matHeaderCellDef
                    class="text-xs uppercase text-secondary font-medium text-right">
                    Sold
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let p"
                    class="text-sm font-semibold text-default text-right">
                    {{ p.totalQuantity }}
                  </td>
                </ng-container>
                <ng-container matColumnDef="revenue">
                  <th
                    mat-header-cell
                    *matHeaderCellDef
                    class="text-xs uppercase text-secondary font-medium text-right">
                    Revenue
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let p"
                    class="text-sm font-semibold text-default text-right">
                    {{ p.totalRevenueMinor | amountToMajor | price }}
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="topProductColumns"></tr>
                <tr
                  mat-row
                  *matRowDef="let row; columns: topProductColumns"
                  class="hover:bg-hover transition-colors"></tr>
                <tr class="mat-row" *matNoDataRow>
                  <td
                    class="mat-cell text-center py-8 text-secondary"
                    [attr.colspan]="topProductColumns.length">
                    No sales data yet
                  </td>
                </tr>
              </table>
            </div>
          } @else {
            <div class="flex flex-col gap-2 flex-auto">
              @for (i of [1, 2, 3, 4, 5]; track i) {
                <app-skeleton-row [columns]="4"></app-skeleton-row>
              }
            </div>
          }
          <a
            [routerLink]="reportPaths.Dashboard"
            class="flex items-center gap-1 text-xs text-primary-600 mt-3 hover:underline">
            <span>View reports</span>
            <mat-icon
              svgIcon="mat:chevron_right"
              class="w-3 h-3"
              style="font-size: 14px"></mat-icon>
          </a>
        </div>
      }
    </div>
  </div>

  <!-- Compliance widgets & Shifts -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    <!-- Shifts -->
    <div *appHasPermission="PERMS.SHIFTS.READ">
      @if (openShifts$ | async; as item) {
        <app-shift-card
          [item]="item"
          (retry)="retryOpenShifts()"></app-shift-card>
      }
    </div>
    <!-- #11 Branches without superintendent -->
    <div *appHasPermission="PERMS.BRANCHES.READ">
      @if (superItem; as item) {
        <app-list-card
          title="No Superintendent"
          icon="mat:supervisor_account"
          colorClass="bg-red-600/10 text-red-600"
          [item]="item"
          [count]="(unsupervisedBranches$ | async)?.length ?? 0"
          [link]="adminPaths.Branches"
          (retry)="retrySuperintendentStatus()">
          @for (b of unsupervisedBranches$ | async; track b.branchId) {
            <div class="border border-divider rounded-lg p-2">
              <p class="text-sm font-medium text-default">{{ b.branchName }}</p>
              <p class="text-xs text-red-600">Unsupervised</p>
            </div>
          } @empty {
            <p class="text-sm text-secondary text-center py-4">
              All branches have a superintendent
            </p>
          }
        </app-list-card>
      }
    </div>

    @if (false) {
      <!-- #12 Branches with expired credentials -->
      <div *appHasPermission="PERMS.BRANCHES.READ">
        @if (superItem; as item) {
          <app-list-card
            title="Expired Credentials"
            icon="mat:badge"
            colorClass="bg-orange-600/10 text-orange-600"
            [item]="item ?? undefined"
            [count]="(expiredCredentialBranches$ | async)?.length ?? 0"
            [link]="adminPaths.Branches"
            (retry)="retrySuperintendentStatus()">
            @for (b of expiredCredentialBranches$ | async; track b.branchId) {
              <div class="border border-divider rounded-lg p-2">
                <p class="text-sm font-medium text-default">
                  {{ b.branchName }}
                </p>
                <p class="text-xs text-orange-600">
                  Superintendent credential expired
                </p>
              </div>
            } @empty {
              <p class="text-sm text-secondary text-center py-4">
                No expired credentials
              </p>
            }
          </app-list-card>
        }
      </div>

      <!-- #13 Users with credentials expiring -->
      <div *appHasPermission="PERMS.USERS.CREDENTIALS_MANAGE">
        @if (usersExpiringCredentials$ | async; as item) {
          <app-list-card
            title="Credentials Expiring"
            icon="mat:assignment_late"
            colorClass="bg-orange-600/10 text-orange-600"
            [item]="item ?? undefined"
            [count]="item?.data?.length ?? 0"
            [link]="userPaths.List"
            (retry)="retryUsersExpiring()">
            @for (u of item?.data; track u.credentialId) {
              <div class="border border-divider rounded-lg p-2">
                <div class="flex items-center justify-between">
                  <p class="text-sm font-medium text-default">
                    {{ u.userName }}
                  </p>
                  <span
                    class="text-xs font-semibold"
                    [class]="
                      u.daysUntilExpiry <= 7
                        ? 'text-red-600'
                        : 'text-orange-600'
                    ">
                    {{ u.daysUntilExpiry }}d
                  </span>
                </div>
                <p class="text-xs text-secondary">
                  {{ u.credentialType }} · {{ u.credentialNumber }} · Expires
                  {{ u.expiresAt | date: "mediumDate" }}
                </p>
              </div>
            } @empty {
              <p class="text-sm text-secondary text-center py-4">
                No credentials expiring soon
              </p>
            }
          </app-list-card>
        }
      </div>
    }
  </div>
</div>
